import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { lazy, Suspense, type ComponentType, useEffect, useState } from "react";
import { trackFullEvent } from "@/lib/tracking";

// Standard imports for main entry pages for maximum reliability
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Cadastro from "./pages/Cadastro";
import Recovery from "./pages/Recovery";
import NotFound from "./pages/NotFound";

const lazyWithRetry = (importer: () => Promise<{ default: ComponentType<any> }>) =>
  lazy(() =>
    importer()
      .then((module) => {
        sessionStorage.removeItem("lazy-import-reload");
        return module;
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : String(error);
        const isDynamicImportError = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(message);

        if (isDynamicImportError && !sessionStorage.getItem("lazy-import-reload")) {
          sessionStorage.setItem("lazy-import-reload", "true");
          window.location.reload();
        }

        throw error;
      })
  );

// Admin Pages
const AdminLayout = lazyWithRetry(() => import("./layouts/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/Dashboard"));
const AdminEmpresas = lazyWithRetry(() => import("./pages/admin/Empresas"));
const AdminPlanos = lazyWithRetry(() => import("./pages/admin/Planos"));
const AdminUsuarios = lazyWithRetry(() => import("./pages/admin/Usuarios"));
const AdminFinanceiro = lazyWithRetry(() => import("./pages/admin/Financeiro"));
const AdminConfiguracoes = lazyWithRetry(() => import("./pages/admin/Configuracoes"));

// Company Pages
const CompanyLayout = lazyWithRetry(() => import("./layouts/CompanyLayout"));
const CompanyDashboard = lazyWithRetry(() => import("./pages/company/Dashboard"));
const Funcionarios = lazyWithRetry(() => import("./pages/company/Funcionarios"));

const Departamentos = lazyWithRetry(() => import("./pages/company/Departamentos"));
const Descontos = lazyWithRetry(() => import("./pages/company/Descontos"));
const Faltas = lazyWithRetry(() => import("./pages/company/Faltas"));
const HorasExtras = lazyWithRetry(() => import("./pages/company/HorasExtras"));
const Folha = lazyWithRetry(() => import("./pages/company/Folha"));
const Ocorrencias = lazyWithRetry(() => import("./pages/company/Ocorrencias"));
const CustoReal = lazyWithRetry(() => import("./pages/company/CustoReal"));
const Configuracoes = lazyWithRetry(() => import("./pages/company/Configuracoes"));
const Assinatura = lazyWithRetry(() => import("./pages/company/Assinatura"));
const FeriasAfastamentos = lazyWithRetry(() => import("./pages/company/FeriasAfastamentos"));

import WhatsAppFloat from "./components/WhatsAppFloat";
import { ProgressBar } from "./components/ProgressBar";
import { ErrorBoundary } from "./components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      gcTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const PageLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-background/50 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-4">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent shadow-lg" />
      <p className="text-sm font-semibold text-muted-foreground animate-pulse">Carregando ambiente...</p>
    </div>
  </div>
);

function AuthRedirect() {
  const { user, role, loading, signOut, refreshRole, error } = useAuth();
  const [retries, setRetries] = useState(0);
  const [showSlowConnection, setShowSlowConnection] = useState(false);
  
  useEffect(() => {
    let timeout: any;
    let slowTimeout: any;

    if (loading && !showSlowConnection) {
      slowTimeout = setTimeout(() => setShowSlowConnection(true), 8000);
    }

    if (user && !role && !loading && !error && retries < 5) {
      timeout = setTimeout(() => {
        setRetries(prev => prev + 1);
        refreshRole();
      }, 2000);
    }
    return () => {
      clearTimeout(timeout);
      clearTimeout(slowTimeout);
    };
  }, [user, role, loading, error, refreshRole, retries, showSlowConnection]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4">
        <PageLoader />
        {showSlowConnection && (
          <div className="mt-8 animate-fade-in text-center max-w-xs">
            <p className="text-sm text-muted-foreground mb-4">A conexão está demorando mais que o esperado...</p>
            <button 
              onClick={() => window.location.reload()}
              className="text-xs font-bold text-primary underline"
            >
              Recarregar página
            </button>
          </div>
        )}
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  if (role === "super_admin") return <Navigate to="/admin" replace />;
  if (role === "empresa_admin" || role === "gestor_rh" || role === "somente_leitura" || role === "funcionario") {
    return <Navigate to="/sistema/dashboard" replace />;
  }

  if (!loading && (!role || error)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
        <div className="max-w-md w-full bg-card p-8 rounded-2xl shadow-xl border border-border/50 text-center animate-fade-in">
          {error ? (
            <div className="h-12 w-12 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold">!</span>
            </div>
          ) : (
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          )}
          
          <h1 className="text-2xl font-bold mb-4 text-foreground">
            {error ? "Erro de conexão" : "Configurando seu acesso..."}
          </h1>
          
          <p className="text-muted-foreground mb-4">
            {error 
              ? "Não conseguimos carregar suas permissões. Por favor, verifique sua internet e tente novamente."
              : "Estamos finalizando a configuração da sua conta. Isso deve levar apenas alguns segundos."}
          </p>
          
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                setRetries(0);
                refreshRole();
              }}
              className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all h-12 shadow-sm"
            >
              Tentar novamente
            </button>
            <button
              onClick={() => signOut()}
              className="w-full inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-semibold border border-input bg-background hover:bg-accent h-12"
            >
              Sair
            </button>
          </div>
          
          {retries > 0 && !error && (
            <p className="mt-4 text-xs text-muted-foreground">
              Tentativa {retries} de 5
            </p>
          )}
        </div>
      </div>
    );
  }

  return <Navigate to="/login" replace />;
}

const AppContent = () => {
  const location = useLocation();

  useEffect(() => {
    // PageView tracking on route change
    trackFullEvent("PageView");
  }, [location.pathname]);

  useEffect(() => {
    // Version check for cache busting if needed
    localStorage.setItem("app-version", "1.0.4");
  }, []);

  return (
    <Suspense fallback={<PageLoader />}>
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/landing" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/cadastro" element={<Cadastro />} />
          <Route path="/recovery" element={<Recovery />} />
          <Route path="/redirect" element={<AuthRedirect />} />

          {/* Super Admin Routes */}
          <Route path="/admin" element={<ProtectedRoute requiredRole="super_admin"><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="empresas" element={<AdminEmpresas />} />
            <Route path="planos" element={<AdminPlanos />} />
            <Route path="usuarios" element={<AdminUsuarios />} />
            <Route path="financeiro" element={<AdminFinanceiro />} />
            <Route path="configuracoes" element={<AdminConfiguracoes />} />
          </Route>

          {/* Company/System Routes */}
          <Route path="/sistema" element={<ProtectedRoute><CompanyLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<CompanyDashboard />} />
            <Route path="funcionarios" element={<Funcionarios />} />
            
            <Route path="departamentos" element={<Departamentos />} />
            <Route path="descontos" element={<Descontos />} />
            <Route path="faltas" element={<Faltas />} />
            <Route path="horas-extras" element={<HorasExtras />} />
            <Route path="folha" element={<Folha />} />
            <Route path="ocorrencias" element={<Ocorrencias />} />
            <Route path="ferias-afastamentos" element={<FeriasAfastamentos />} />
            <Route path="custo-real" element={<CustoReal />} />
            <Route path="assinatura" element={<Assinatura />} />
            <Route path="configuracoes" element={<Configuracoes />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </Suspense>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner position="top-right" closeButton richColors />
        <BrowserRouter>
          <ProgressBar />
          <AuthProvider>
            <AppContent />
            <WhatsAppFloat />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
